/*
  Port of Prestige's real scroll-carousel + carousel-prev-button/carousel-next-button
  custom elements (theme id 192185991444, "20" -- js/common/carousel/scroll-carousel.js
  and carousel-navigation.js), rewritten with plain private class fields instead of the
  original's build-tool-transpiled __privateGet/__privateSet helpers, and with the
  "vendor" bundle's image-preload-on-inView call replaced by a plain IntersectionObserver
  (no other behavior change) since that bundle isn't part of this theme.
*/

if (!customElements.get('scroll-carousel')) {
  customElements.define(
    'scroll-carousel',
    class ScrollCarousel extends HTMLElement {
      #targetIndex = 0;
      #forceChangeEvent = false;
      #hasPendingProgrammaticScroll = false;
      #isDragging = false;
      #dragPosition = {};
      #scrollTimeout;
      #dispatchable = { nearingStart: true, nearingEnd: true, leavingStart: true, leavingEnd: true };
      #onMouseDown = this.#handleMouseDown.bind(this);
      #onMouseMove = this.#handleMouseMove.bind(this);
      #onMouseClick = this.#handleMouseClick.bind(this);
      #onMouseUp = this.#handleMouseUp.bind(this);

      constructor() {
        super();
        new ResizeObserver(this.#handleResize.bind(this)).observe(this);
        new MutationObserver(() => (this.#forceChangeEvent = true)).observe(this, {
          subtree: true,
          attributes: true,
          attributeFilter: ['hidden'],
        });
      }

      connectedCallback() {
        if (this.allCells.length > 1) {
          this.addEventListener('scroll', this.#handleScroll.bind(this), { passive: true });
          this.addEventListener('scrollend', this.#handleScrollEnd.bind(this));
          if (this.hasAttribute('allow-drag')) {
            const mediaQuery = window.matchMedia('screen and (pointer: fine)');
            const syncDragListener = () => {
              if (mediaQuery.matches) this.addEventListener('mousedown', this.#onMouseDown);
              else this.removeEventListener('mousedown', this.#onMouseDown);
            };
            mediaQuery.addEventListener('change', syncDragListener);
            syncDragListener();
          }
        }

        const initialIndex = this.cells.findIndex((item) => item.classList.contains('is-initial'));
        if (initialIndex > 0) {
          this.#targetIndex = initialIndex;
          this.select(initialIndex, { instant: true });
        }

        new IntersectionObserver((entries, observer) => {
          if (entries[0].isIntersecting) {
            this.#preloadImages();
            observer.disconnect();
          }
        }).observe(this);
      }

      disconnectedCallback() {
        this.removeEventListener('mousemove', this.#onMouseMove);
        document.removeEventListener('mouseup', this.#onMouseUp);
      }

      get cellSelector() {
        return this.getAttribute('cell-selector');
      }

      get allCells() {
        return this.cellSelector ? Array.from(this.querySelectorAll(this.cellSelector)) : Array.from(this.children);
      }

      get cells() {
        return this.allCells.filter((cell) => !cell.hasAttribute('hidden'));
      }

      get selectedCell() {
        return this.cells[this.selectedIndex];
      }

      get selectedIndex() {
        return this.#targetIndex;
      }

      get cellAlign() {
        const align = this.cells[0] ? getComputedStyle(this.cells[0]).scrollSnapAlign : 'start';
        return align === 'none' ? 'center' : align;
      }

      get groupCells() {
        if (this.hasAttribute('group-cells')) {
          const number = parseInt(this.getAttribute('group-cells'));
          return isNaN(number) ? Math.floor(this.clientWidth / this.cells[0].clientWidth) : number;
        }
        return 1;
      }

      get isScrollable() {
        return this.scrollWidth - this.clientWidth > 1 || this.scrollHeight - this.clientHeight > 1;
      }

      previous({ instant = false } = {}) {
        this.select(Math.max(this.#targetIndex - this.groupCells, 0), { instant });
      }

      next({ instant = false } = {}) {
        this.select(Math.min(this.#targetIndex + this.groupCells, this.cells.length - 1), { instant });
      }

      select(index, { instant = false } = {}) {
        if (!(index in this.cells)) return;
        this.dispatchEvent(new CustomEvent('carousel:select', { detail: { index, cell: this.cells[index] } }));
        const visible = this.offsetWidth > 0 && this.offsetHeight > 0;
        if (visible) {
          const targetScrollLeft = this.#calculateLeftScroll(this.cells[index]);
          if (this.scrollLeft !== targetScrollLeft) {
            this.#updateTargetIndex(index);
            this.#hasPendingProgrammaticScroll = true;
            this.scrollTo({ left: targetScrollLeft, behavior: instant ? 'auto' : 'smooth' });
          } else {
            this.#updateTargetIndex(this.#calculateClosestIndex());
          }
        } else {
          this.#targetIndex = index;
          this.#forceChangeEvent = true;
        }
      }

      #updateTargetIndex(newValue) {
        if (newValue === this.#targetIndex && !this.#forceChangeEvent) return;
        this.#targetIndex = newValue;
        this.#forceChangeEvent = false;
        this.dispatchEvent(new CustomEvent('carousel:change', { detail: { index: newValue, cell: this.cells[newValue] } }));
      }

      #handleScroll() {
        const threshold = 100;
        const scrollLeft = Math.round(Math.abs(this.scrollLeft));

        if (scrollLeft < threshold && this.#dispatchable.nearingStart) {
          this.dispatchEvent(new CustomEvent('scroll:edge-nearing', { detail: { position: 'start' } }));
          this.#dispatchable.nearingStart = false;
          this.#dispatchable.leavingStart = true;
        }
        if (scrollLeft >= threshold && this.#dispatchable.leavingStart) {
          this.dispatchEvent(new CustomEvent('scroll:edge-leaving', { detail: { position: 'start' } }));
          this.#dispatchable.leavingStart = false;
          this.#dispatchable.nearingStart = true;
        }
        if (this.scrollWidth - this.clientWidth < scrollLeft + threshold && this.#dispatchable.nearingEnd) {
          this.dispatchEvent(new CustomEvent('scroll:edge-nearing', { detail: { position: 'end' } }));
          this.#dispatchable.nearingEnd = false;
          this.#dispatchable.leavingEnd = true;
        }
        if (this.scrollWidth - this.clientWidth >= scrollLeft + threshold && this.#dispatchable.leavingEnd) {
          this.dispatchEvent(new CustomEvent('scroll:edge-leaving', { detail: { position: 'end' } }));
          this.#dispatchable.leavingEnd = false;
          this.#dispatchable.nearingEnd = true;
        }

        if (!('onscrollend' in window)) {
          clearTimeout(this.#scrollTimeout);
          this.#scrollTimeout = setTimeout(() => this.dispatchEvent(new CustomEvent('scrollend', { bubbles: true })), 75);
        }

        if (this.#hasPendingProgrammaticScroll) return;
        this.#updateTargetIndex(this.#calculateClosestIndex());
      }

      #handleScrollEnd() {
        this.#hasPendingProgrammaticScroll = false;
        if (!this.#isDragging) this.style.removeProperty('scroll-snap-type');
        this.#updateTargetIndex(this.#calculateClosestIndex());
      }

      #calculateLeftScroll(cell) {
        let scrollLeft;
        switch (this.cellAlign) {
          case 'start':
            scrollLeft = cell.offsetLeft;
            break;
          case 'end':
            scrollLeft = cell.offsetLeft + cell.offsetWidth - this.clientWidth;
            break;
          default:
            scrollLeft = Math.round(cell.offsetLeft - this.clientWidth / 2 + cell.clientWidth / 2);
        }
        return Math.min(Math.max(scrollLeft, 0), this.scrollWidth - this.clientWidth);
      }

      #calculateClosestIndex() {
        let offsets, targetPoint;
        if (this.cellAlign === 'center') {
          offsets = this.cells.map((cell) => Math.round(cell.offsetLeft + cell.clientWidth / 2));
          targetPoint = Math.round(this.scrollLeft + this.clientWidth / 2);
        } else if (this.cellAlign === 'start') {
          offsets = this.cells.map((cell) => cell.offsetLeft);
          targetPoint = this.scrollLeft;
        } else {
          offsets = this.cells.map((cell) => cell.offsetLeft + cell.clientWidth);
          targetPoint = this.scrollLeft + this.clientWidth;
        }
        return offsets.indexOf(offsets.reduce((prev, curr) => (Math.abs(curr - targetPoint) < Math.abs(prev - targetPoint) ? curr : prev)));
      }

      #handleMouseDown(event) {
        this.#dragPosition = { left: this.scrollLeft, top: this.scrollTop, x: event.clientX, y: event.clientY };
        this.#isDragging = true;
        this.style.setProperty('scroll-snap-type', 'none');
        this.addEventListener('mousemove', this.#onMouseMove);
        this.addEventListener('click', this.#onMouseClick, { once: true });
        document.addEventListener('mouseup', this.#onMouseUp);
      }

      #handleMouseMove(event) {
        event.preventDefault();
        const dx = event.clientX - this.#dragPosition.x;
        const dy = event.clientY - this.#dragPosition.y;
        this.scrollTop = this.#dragPosition.top - dy;
        this.scrollLeft = this.#dragPosition.left - dx;
      }

      #handleMouseClick(event) {
        if (event.clientX - this.#dragPosition.x !== 0) event.preventDefault();
      }

      #handleMouseUp(event) {
        this.#isDragging = false;
        if (event.clientX - this.#dragPosition.x === 0) {
          this.style.removeProperty('scroll-snap-type');
        } else if (!this.#hasPendingProgrammaticScroll) {
          this.scrollTo({ left: this.#calculateLeftScroll(this.selectedCell), behavior: 'smooth' });
        }
        this.removeEventListener('mousemove', this.#onMouseMove);
        document.removeEventListener('mouseup', this.#onMouseUp);
      }

      #handleResize() {
        if (this.selectedIndex !== this.#calculateClosestIndex()) {
          this.select(this.selectedIndex, { instant: true });
        }
        this.classList.toggle('is-scrollable', this.scrollWidth > this.clientWidth);
      }

      #preloadImages() {
        requestAnimationFrame(() => {
          const prev = this.cells[Math.max(this.selectedIndex - 1, 0)];
          const next = this.cells[Math.min(this.selectedIndex + 1, this.cells.length - 1)];
          [prev, this.selectedCell, next]
            .filter(Boolean)
            .forEach((item) => {
              item.querySelectorAll('img[loading="lazy"]').forEach((img) => img.removeAttribute('loading'));
            });
        });
      }
    }
  );
}

if (!customElements.get('carousel-prev-button')) {
  customElements.define(
    'carousel-prev-button',
    class extends HTMLElement {
      #abortController;

      connectedCallback() {
        if (!this.carousel) return;
        this.#abortController = new AbortController();
        this.addEventListener('click', () => this.carousel.previous(), { signal: this.#abortController.signal });
        this.carousel.addEventListener(
          'scroll:edge-nearing',
          (event) => (this.firstElementChild.disabled = event.detail.position === 'start'),
          { signal: this.#abortController.signal }
        );
        this.carousel.addEventListener(
          'scroll:edge-leaving',
          (event) => {
            if (event.detail.position === 'start') this.firstElementChild.disabled = false;
          },
          { signal: this.#abortController.signal }
        );
      }

      disconnectedCallback() {
        this.#abortController?.abort();
      }

      get carousel() {
        return document.getElementById(this.getAttribute('aria-controls'));
      }
    }
  );
}

if (!customElements.get('carousel-next-button')) {
  customElements.define(
    'carousel-next-button',
    class extends HTMLElement {
      #abortController;

      connectedCallback() {
        if (!this.carousel) return;
        this.#abortController = new AbortController();
        this.addEventListener('click', () => this.carousel.next(), { signal: this.#abortController.signal });
        this.carousel.addEventListener(
          'scroll:edge-nearing',
          (event) => (this.firstElementChild.disabled = event.detail.position === 'end'),
          { signal: this.#abortController.signal }
        );
        this.carousel.addEventListener(
          'scroll:edge-leaving',
          (event) => {
            if (event.detail.position === 'end') this.firstElementChild.disabled = false;
          },
          { signal: this.#abortController.signal }
        );
      }

      disconnectedCallback() {
        this.#abortController?.abort();
      }

      get carousel() {
        return document.getElementById(this.getAttribute('aria-controls'));
      }
    }
  );
}

/* Multi-collection tab-switching (featured-collections-carousel) isn't exercised by
   any section on this site -- every "featured-collections" instance (Más Vendidos,
   Hombre, Mujer) has exactly one collection block, so the CSS's own
   :not(.is-selected){display:none} rule always shows the single item with no JS
   needed. Registered as a no-op custom element only so the tag isn't left undefined. */
if (!customElements.get('featured-collections-carousel')) {
  customElements.define('featured-collections-carousel', class extends HTMLElement {});
}
